import { useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { FinancialDecision, IVehicle } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";

const DECISION_OPTION_VALUES: { value: FinancialDecision; labelKey: string; active: string; idle: string }[] = [
  { value: FinancialDecision.None, labelKey: "None", active: "border-transparent bg-gray-600 text-white hover:bg-gray-700", idle: "border-gray-400 text-gray-700 hover:bg-gray-50" },
  { value: FinancialDecision.Target, labelKey: "Target", active: "border-transparent bg-green-600 text-white hover:bg-green-700", idle: "border-green-500 text-green-700 hover:bg-green-50" },
  { value: FinancialDecision.Rejected, labelKey: "Rejected", active: "border-transparent bg-red-600 text-white hover:bg-red-700", idle: "border-red-500 text-red-700 hover:bg-red-50" },
  { value: FinancialDecision.Purchased, labelKey: "Purchased", active: "border-transparent bg-blue-600 text-white hover:bg-blue-700", idle: "border-blue-500 text-blue-700 hover:bg-blue-50" },
];

export default function BulkFinancialDecisionModal({
  open,
  vehicles,
  onClose,
}: {
  open: boolean;
  vehicles: IVehicle[];
  onClose: () => void;
}) {
  const { t } = useTranslation("vehicles");
  const { patchVehicle } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Financial Decision"));
  const queryClient = useQueryClient();

  const [decision, setDecision] = useState<FinancialDecision | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: number; failed: number } | null>(null);

  useEffect(() => {
    if (!open) {
      setDecision(null);
      setLoading(false);
      setResult(null);
    }
  }, [open]);

  const apply = async () => {
    if (decision === null || vehicles.length === 0) return;
    setLoading(true);

    const vehicleIds = new Set(vehicles.map((v) => v.id));

    // Optimistically update all matching paged table caches before the server round-trip
    queryClient.setQueriesData(["pagedTable"], (oldData: any) => {
      if (!oldData?.data?.rows) return oldData;
      return {
        ...oldData,
        data: {
          ...oldData.data,
          rows: oldData.data.rows.map((row: any) =>
            vehicleIds.has(row.id) ? { ...row, financialDecision: decision } : row
          ),
        },
      };
    });

    const results = await Promise.allSettled(
      vehicles.map((vehicle) => patchVehicle.mutateAsync({ vehicleId: vehicle.id, patch: { financialDecision: decision } }))
    );

    const success = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - success;

    // Refresh from server to reconcile any discrepancies
    queryClient.invalidateQueries(["pagedTable"]);

    setLoading(false);
    setResult({ success, failed });
    if (failed === 0) saveHandlers.onSuccess();
    else saveHandlers.onError();
  };

  return (
    <Modal open={open} setOpen={() => onClose()} width="max-w-lg">
      <h1 className="text-xl">{t("Update Financial Decision")}</h1>
      <hr className="my-2" />

      {loading ? (
        <div className="py-8">
          <LoadingWheel />
        </div>
      ) : result ? (
        <div className="mt-2">
          <p>
            {result.success === 1
              ? t("Updated {{count}} vehicle.", { count: result.success })
              : t("Updated {{count}} vehicles.", { count: result.success })}
            {result.failed > 0 && <> {t("{{count}} failed.", { count: result.failed })}</>}
          </p>
          <div className="mt-4 flex justify-end">
            <ButtonInput label={t("Close")} onClick={onClose} classes="" />
          </div>
        </div>
      ) : (
        <div className="mt-2">
          <p>
            {vehicles.length === 1
              ? t("Set the financial decision for {{count}} selected vehicle.", { count: vehicles.length })
              : t("Set the financial decision for {{count}} selected vehicles.", { count: vehicles.length })}
          </p>

          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {DECISION_OPTION_VALUES.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setDecision(option.value)}
                className={classNames(
                  "rounded-md border px-3 py-2 text-sm font-semibold transition-colors",
                  decision === option.value ? option.active : option.idle
                )}
              >
                {t(option.labelKey)}
              </button>
            ))}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t("Cancel")}
            </button>
            <ButtonInput label={t("Apply")} onClick={apply} disabled={decision === null || vehicles.length === 0} classes="" />
          </div>
        </div>
      )}
    </Modal>
  );
}
